import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import IncomingShipmentItemPhotoCell from './IncomingShipmentItemPhotoCell';
import IncomingShipmentQuantityField from './IncomingShipmentQuantityField';
import IncomingShipmentExpectedBlock from './IncomingShipmentExpectedBlock';
import { incomingShipmentApi } from '../../../../api/incomingShipmentApi';
import { scannedItemStatusBadgeClass, scannedItemStatusLabel } from './incomingShipmentScannedStatusUi';
import { shipmentStatusLabel, shipmentStatusBadgeClass, SHIPMENT_STATUS_SELECT_ORDER } from './incomingShipmentStatusUi';
import IncomingStatusBadgePicker from './IncomingStatusBadgePicker';
import { allocateIncomingScans, incomingBulkReadiness } from '../../../../utils/incomingScanAllocation';

const IncomingShipmentDetailDesktopView = ({
  shipmentId,
  shipment,
  barcode,
  setBarcode,
  submitScan,
  inputRef,
  submitting,
  loading,
  error,
  bindItemId,
  bindSearch,
  setBindSearch,
  bindSearchResults,
  bindSearchLoading,
  bindSubmitting,
  openBindDropdown,
  bindProductToItem,
  itemPhotoUploadingId,
  uploadItemPhoto,
  deleteItemPhoto,
  deleteScannedItem,
  saveItemQuantity,
  itemDeletingId,
  itemQuantitySavingId,
  onAddExpectedItem,
  onDeleteExpectedItem,
  addingExpected,
  deletingExpectedId,
  onOpenExcelImport,
  onSaveExpectedBarcode,
  savingExpectedBarcodeId,
  onBindExpectedItem,
  bindingExpectedId,
  onPatchShipmentStatus,
  savingShipmentStatus,
  onChangeExpectedItemStatus,
  savingExpectedStatusId,
  onPostExpectedToSite,
  postingExpectedToSiteId,
  onPostAllReadyToSite,
  postingAllReady,
  expectedDeleteConfirmOpen,
  onGenerateExpectedFromScanned,
  generatingExpectedFromScanned,
  onSaveExpectedPurchasePrice,
  savingExpectedPurchasePriceId
}) => {
  const shipmentStatusOptionsFor = (current) => {
    const cur = current || 'draft';
    const set = new Set(SHIPMENT_STATUS_SELECT_ORDER);
    const list = [...SHIPMENT_STATUS_SELECT_ORDER];
    if (cur && !set.has(cur)) list.unshift(cur);
    return list;
  };
  const scannedItems = useMemo(() => shipment?.scannedItems || [], [shipment?.scannedItems]);
  const expectedItems = useMemo(() => shipment?.expectedItems || [], [shipment?.expectedItems]);
  const [scannedOpen, setScannedOpen] = useState(true);
  const [downloadBusy, setDownloadBusy] = useState(false);

  const files = shipment?.files || [];
  const allAwsNames = files.map((f) => f?.awsName).filter(Boolean);
  const uniqueAwsNames = Array.from(new Set(allAwsNames.map((x) => String(x))));

  const downloadSourceFile = (awsName) => {
    const name = (awsName || '').trim();
    if (!name || downloadBusy) return;
    setDownloadBusy(true);
    incomingShipmentApi.downloadSourceFile(shipmentId, name)
      .then((res) => {
        const blob = res.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = String(name || `incoming-shipment-${shipmentId}.xlsx`);
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {})
      .finally(() => setDownloadBusy(false));
  };

  const scanAllocation = useMemo(() => allocateIncomingScans(expectedItems, scannedItems), [expectedItems, scannedItems]);
  const scanSummary = scanAllocation.summary;
  const bulkPostReady = useMemo(() => incomingBulkReadiness(expectedItems, scanAllocation), [expectedItems, scanAllocation]);

  return (
    <>
      <div className='d-flex justify-content-between align-items-start align-items-md-center mt-2 mb-3 flex-wrap' style={{ gap: '0.75rem' }}>
        <div className='flex-grow-1' style={{ minWidth: '12rem' }}>
          <h3 className='font-weight-bold m-0'>Поставка: {shipment?.name || `#${shipmentId}`}</h3>
          {onPatchShipmentStatus ? (
            <div className='d-flex flex-wrap align-items-center mt-2' style={{ gap: '0.5rem' }}>
              <span className='text-muted small mb-0'>Статус поставки</span>
              <IncomingStatusBadgePicker
                value={shipment?.status || 'draft'}
                options={shipmentStatusOptionsFor(shipment?.status)}
                labelOf={shipmentStatusLabel}
                badgeClassOf={shipmentStatusBadgeClass}
                onPick={onPatchShipmentStatus}
                disabled={Boolean(savingShipmentStatus)}
                title='Статус поставки — натисніть, щоб змінити'
              />
            </div>
          ) : null}
        </div>
        <div className='d-flex align-items-center' style={{ gap: '0.5rem' }}>
          {uniqueAwsNames.length ? (
            <div className='d-flex flex-wrap align-items-center' style={{ gap: '0.35rem' }}>
              {uniqueAwsNames.slice(0, 4).map((awsName) => (
                <button
                  key={awsName}
                  type='button'
                  className='btn btn-outline-primary btn-sm'
                  onClick={() => downloadSourceFile(awsName)}
                  disabled={downloadBusy}
                  title='Скачати файл'
                >
                  {downloadBusy ? '…' : 'Скачати файл'}
                </button>
              ))}
              {uniqueAwsNames.length > 4 ? (
                <span className='small text-muted'>ще {uniqueAwsNames.length - 4}</span>
              ) : null}
            </div>
          ) : null}
          <Link to='/admin/incoming-shipments' className='btn btn-outline-dark btn-sm'>До списку</Link>
        </div>
      </div>

      <div className='alert alert-light border py-2 px-3 mb-3'>
        <div
          className='d-flex flex-wrap align-items-center justify-content-between'
          style={{ gap: '0.5rem 0.75rem' }}
        >
          <div className='d-flex flex-wrap align-items-center' style={{ gap: '0.5rem 1rem', fontSize: '1.05rem' }}>
            <div className='font-weight-bold text-dark'>Результат сканування</div>
            <div>
              <span className='badge badge-success mr-1'>Повністю</span>
              <span className='font-weight-bold'>{scanSummary.full}</span> / {scanSummary.expectedCount}
            </div>
            <div>
              <span className='badge badge-warning mr-1'>Частково</span>
              <span className='font-weight-bold'>{scanSummary.partial}</span>
            </div>
            <div>
              <span className='badge badge-danger mr-1'>Більше</span>
              <span className='font-weight-bold'>{scanSummary.over}</span>
            </div>
            <div>
              <span className='text-muted'>К-ть</span>{' '}
              <span className='font-weight-bold'>{scanSummary.scannedQtyMatchedTotal}</span> / {scanSummary.expectedQtyTotal}
            </div>
          </div>
          {onPostAllReadyToSite && bulkPostReady.show ? (
            <button
              type='button'
              className='btn btn-sm btn-success flex-shrink-0'
              onClick={() => void onPostAllReadyToSite()}
              disabled={Boolean(
                !bulkPostReady.canPost || postingAllReady || postingExpectedToSiteId != null || addingExpected || deletingExpectedId != null || savingExpectedPurchasePriceId != null || bindingExpectedId != null || savingExpectedBarcodeId != null || savingExpectedStatusId != null
              )}
              title={bulkPostReady.reason || `Внести на склад усі готові позиції (${bulkPostReady.count})`}
            >
              {postingAllReady ? '…' : 'Внести все'}
            </button>
          ) : null}
        </div>
      </div>

      {scanSummary.unmatchedQuantity > 0 && <div className='alert alert-warning' role='status'>
        Невраховані скани: <b>{scanSummary.unmatchedQuantity} шт.</b> Зіставте їх з очікуваними товарами перед завершенням поставки.
      </div>}
      {bulkPostReady.show && bulkPostReady.reason && <p className='text-danger small' role='status'>{bulkPostReady.reason}</p>}

      {error ? <div className='alert alert-danger mt-3'>{error}</div> : null}

      <IncomingShipmentExpectedBlock
        items={shipment?.expectedItems}
        scannedItems={shipment?.scannedItems}
        onAddItem={onAddExpectedItem}
        onDeleteItem={onDeleteExpectedItem}
        adding={addingExpected}
        deletingId={deletingExpectedId}
        onOpenExcelImport={onOpenExcelImport}
        onSaveExpectedBarcode={onSaveExpectedBarcode}
        savingBarcodeId={savingExpectedBarcodeId}
        onBindExpectedItem={onBindExpectedItem}
        bindingExpectedId={bindingExpectedId}
        onChangeExpectedItemStatus={onChangeExpectedItemStatus}
        savingExpectedStatusId={savingExpectedStatusId}
        onPostExpectedToSite={onPostExpectedToSite}
        postingExpectedToSiteId={postingExpectedToSiteId}
        expectedDeleteConfirmOpen={expectedDeleteConfirmOpen}
        onSaveExpectedPurchasePrice={onSaveExpectedPurchasePrice}
        savingPurchasePriceId={savingExpectedPurchasePriceId}
        incomingToolbarBusy={Boolean(postingAllReady || postingExpectedToSiteId != null || loading)}
        onGenerateExpectedFromScanned={onGenerateExpectedFromScanned}
        generatingExpectedFromScanned={generatingExpectedFromScanned}
      />

      <div className='card mb-3'>
        <div
          className='card-header d-flex align-items-center py-2'
          style={{ cursor: 'pointer' }}
          onClick={() => setScannedOpen(!scannedOpen)}
          role='button'
          tabIndex={0}
          aria-expanded={scannedOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setScannedOpen(!scannedOpen);
            }
          }}
        >
          <span
            className='mr-2 font-weight-bold text-dark'
            style={{ fontSize: '1.25rem', lineHeight: 1, userSelect: 'none' }}
            aria-hidden
          >
            {scannedOpen ? '−' : '+'}
          </span>
          <span className='font-weight-bold mb-0'>Проскановані товари</span>
          <span className='text-muted small ml-2'>({scannedItems.length})</span>
        </div>
        {scannedOpen ? (
          <div className='card-body py-3'>
          <label className='font-weight-bold mb-1 d-block'>Сканування штрихкоду</label>
          <input
            ref={inputRef}
            type='text'
            className='form-control form-control-lg'
            placeholder='Скануйте штрихкод'
            value={barcode}
            onChange={(e) => {
              const value = e.target.value || '';
              if (value.includes('\n') || value.includes('\r')) {
                const normalized = value.replace(/\r/g, '\n');
                const firstCode = normalized.split('\n').find((part) => part.trim() !== '');
                if (firstCode) submitScan(firstCode.trim());
                setBarcode('');
                return;
              }
              setBarcode(value);
            }}
            onKeyDown={(e) => e.key === 'Enter' && submitScan()}
            disabled={loading}
          />

          {loading && !shipment ? (
            <div className='mt-2'>Завантаження...</div>
          ) : (
            <div className='table-responsive mt-2'>
              <table className='table table-sm mb-0'>
                <thead>
                  <tr>
                    <th>Штрих-код</th>
                    <th>Артикул</th>
                    <th>Назва</th>
                    <th>Кількість</th>
                    <th>Статус</th>
                    <th>Коли проскановано</th>
                    <th className='text-right'>Дії</th>
                  </tr>
                </thead>
                <tbody>
                  {scannedItems.map((it) => (
                    <tr key={it.id}>
                      <td className='text-monospace'>{it.barcode}</td>
                      <td className='text-monospace'>{it.productCode || ''}</td>
                      <td>
                        {it.foundInCatalog ? (
                          <div className='d-flex align-items-center flex-wrap incoming-receipt-name-line' style={{ gap: '0.35rem' }}>
                            <span className='mb-0 text-break' style={{ minWidth: 0 }}>{it.name || ''}</span>
                            <IncomingShipmentItemPhotoCell
                              item={it}
                              placement='afterName'
                              uploading={itemPhotoUploadingId === it.id}
                              onSelectFile={(file) => uploadItemPhoto(it.id, file)}
                              onDelete={() => deleteItemPhoto(it.id)}
                            />
                          </div>
                        ) : (
                          <>
                            <div>{it.name || ''}</div>
                            <div className='d-flex align-items-center flex-nowrap mt-1 incoming-receipt-name-actions'>
                              <button
                                type='button'
                                className='btn btn-sm btn-outline-dark flex-shrink-0'
                                onClick={() => openBindDropdown(it.id)}
                                disabled={bindSubmitting}
                                data-bind-open-btn='true'
                              >
                                Привʼязати
                              </button>
                              <IncomingShipmentItemPhotoCell
                                item={it}
                                placement='withBind'
                                uploading={itemPhotoUploadingId === it.id}
                                onSelectFile={(file) => uploadItemPhoto(it.id, file)}
                                onDelete={() => deleteItemPhoto(it.id)}
                              />
                            </div>
                          </>
                        )}
                        {!it.foundInCatalog && bindItemId === it.id ? (
                          <div className='card mt-2'>
                            <div className='card-body p-2'>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                placeholder='Пошук товару (мін. 2 символи)'
                                value={bindSearch}
                                onChange={(e) => setBindSearch(e.target.value)}
                                disabled={bindSubmitting}
                                data-bind-search-input='true'
                              />
                              <div className='mt-2'>
                                {bindSearchLoading ? (
                                  <div className='small text-muted'>Пошук...</div>
                                ) : null}
                                {!bindSearchLoading && (bindSearchResults || []).slice(0, 8).map((p) => (
                                  <button
                                    key={p.id}
                                    type='button'
                                    className='dropdown-item px-2 py-1'
                                    onClick={() => bindProductToItem(p.id)}
                                    disabled={bindSubmitting}
                                  >
                                    <span className='font-weight-bold'>{p.code || p.productCode || `#${p.id}`}</span>
                                    {' — '}
                                    <span>{p.name}</span>
                                  </button>
                                ))}
                                {!bindSearchLoading && bindSearch.trim().length >= 2 && !(bindSearchResults || []).length ? (
                                  <div className='small text-muted'>Нічого не знайдено</div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </td>
                      <td data-incoming-item-action='true'>
                        <IncomingShipmentQuantityField
                          itemId={it.id}
                          quantity={it.quantity}
                          saving={itemQuantitySavingId === it.id || itemDeletingId === it.id}
                          onSave={(q) => saveItemQuantity(it.id, q)}
                        />
                      </td>
                      <td>
                        <span className={scannedItemStatusBadgeClass(it.scanStatus)} title={it.scanStatus || ''}>
                          {scannedItemStatusLabel(it.scanStatus)}
                        </span>
                      </td>
                      <td>{it.scannedAt || '-'}</td>
                      <td className='text-right text-nowrap' data-incoming-item-action='true'>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-danger'
                          title='Видалити позицію'
                          onClick={() => deleteScannedItem(it.id)}
                          disabled={itemDeletingId === it.id || bindSubmitting || itemQuantitySavingId === it.id}
                        >
                          <i className='bi bi-trash' aria-hidden />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!scannedItems.length ? (
                    <tr>
                      <td colSpan={7} className='text-muted'>Просканованих позицій ще немає</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default IncomingShipmentDetailDesktopView;
